import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/Badge";
import { TimecodeChip } from "@/components/ui/TimecodeChip";
import { CloseIcon, FilmIcon, PencilIcon } from "./icons";

// ════════════════════════════════════════════════════════════════════════
// ComposerDrawer — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The bottom slide-up drawer that hosts the note / clip composer.
// Ports the standalone reference's composer drawer: it's opened on
// demand ("Mark moment" / "Add details" after "Make clip" enters
// clip mode / M), not always-visible — the v1
// layout kept the composer permanently mounted in the canvas's
// toolbar rail.
//
// The drawer is `position: fixed` along the bottom edge, capped near
// the viewport height with its own scroll. The header carries a kind badge (NEW
// NOTE / NEW CLIP) + a timecode chip (the playhead for a note, the
// clip range for a clip) + a Close button. The body is the caller's
// composer node.
//
// Esc closes (the parent also clears clip mode on close where
// relevant). Reuses <Badge> / <TimecodeChip> + the v2 inline icons —
// no new tokens, no rebuilt primitives.
// ════════════════════════════════════════════════════════════════════════

export type ComposerDrawerProps = {
  /** "note" → coaching note / observation; "clip" → video clip. */
  kind: "note" | "clip";
  /** Existing feedback opens in edit mode; new feedback opens in create mode. */
  editing?: boolean;
  /** Timecode line shown in the header — the playhead readout for a
   * note, or the "IN → OUT · DUR" range for a clip. */
  timecodeLabel: string;
  /** Close the drawer (the X button + Esc both call this). */
  onClose: () => void;
  /** The composer body — the note or clip composer stack. */
  children: React.ReactNode;
};

export function ComposerDrawer({
  kind,
  editing = false,
  timecodeLabel,
  onClose,
  children,
}: ComposerDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Esc closes the drawer. Doesn't preventDefault — the review
  // keyboard hook also reacts to Esc (disarm tools) and both landing
  // is fine. Scoped to keydown while the drawer is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector('[data-testid="feedback-player-modal"]')) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Move focus into the drawer on open so keyboard users land inside
  // it; the parent restores focus to the trigger on close.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={
        editing
          ? kind === "clip"
            ? "Edit clip details"
            : "Edit feedback details"
          : kind === "clip"
            ? "New clip"
            : "New note"
      }
      tabIndex={-1}
      data-testid="coach-review-composer-drawer"
      className="fixed inset-x-0 bottom-0 z-50 max-h-[calc(100dvh-3rem)] overflow-y-auto border-t border-rule bg-surface shadow-coach focus:outline-none lg:max-h-[78vh]"
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-rule px-4 py-3 sm:px-6">
        <Badge variant={kind === "clip" ? "accent" : "team"}>
          {kind === "clip" ? (
            <>
              <FilmIcon size={10} /> {editing ? "Edit clip" : "New clip"}
            </>
          ) : (
            <>
              <PencilIcon size={10} /> {editing ? "Edit details" : "New note"}
            </>
          )}
        </Badge>
        <TimecodeChip>{timecodeLabel}</TimecodeChip>
        <button
          type="button"
          onClick={onClose}
          data-testid="coach-review-composer-drawer-close"
          className="ml-auto inline-flex items-center gap-1.5 rounded px-2.5 py-1 font-display text-xs uppercase tracking-wide text-ink-secondary transition-colors hover:bg-field hover:text-ink"
        >
          <CloseIcon size={14} />
          Close
          <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
            Esc
          </kbd>
        </button>
      </div>
      <div className="px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-5">{children}</div>
    </div>
  );
}
